import React, { useEffect, useState } from 'react';
import { Alert, Button, Empty, Form, Input, Pagination, Rate, Select, Skeleton, Statistic, Tag } from 'antd';
import { Link, Navigate, useLocation } from 'react-router-dom';
import FeaturePage from '@/components/featurepage';
import PullToRefresh from '@/components/pulltorefresh';
import UserIdentity from '@/components/useridentity';
import { fetchMyReviews, fetchReviewSummary } from '@/apis/posts';
import usePrivateList from '@/utils/usePrivateList';
import useMobile from '@/utils/useMobile';
import { compactQueryParams, isResourceId, resourceIdRule } from '@/utils/queryParams';
import './index.scss';

const loadReviews = async params => {
  const [list, summary] = await Promise.all([fetchMyReviews(params), fetchReviewSummary()]);
  if (!Array.isArray(list.data) || !list.pagination) throw new Error('评价列表响应无效');
  return { ...list, summary };
};
const MyReviews = () => {
  const location = useLocation();
  const postId = new URLSearchParams(location.search).get('post_id');
  const [page, setPage] = useState(1);
  const [filters, setFilters] = useState(() => isResourceId(postId) ? { post_id: postId } : {});
  const [form] = Form.useForm();
  useEffect(() => {
    const next = isResourceId(postId) ? { post_id: postId } : {};
    setFilters(next); setPage(1); form.resetFields(); form.setFieldsValue(next);
  }, [postId, form]);
  const mobile = useMobile();
  const { data, loading, error, load, isAuthenticated } = usePrivateList(loadReviews, { ...filters, page, pageSize: 10 }, '评价加载失败');
  if (!isAuthenticated) return <Navigate to="/login" replace />;
  return <FeaturePage embedded showDesktopBack={false} className="myReviews personalWorkspace" title="我的评价" subtitle="查看本人提交的评价和双方已揭示的收到评价。">
    <PullToRefresh enabled={mobile} disabled={loading} onRefresh={load} successMessage="评价已刷新">
      <section className="featurePage-panel workspace-toolbar">
        {data?.summary && <div className="workspace-summary">{Object.entries({ submitted: '我提交的', received: '我收到的', unrevealed_submitted: '本人待揭示', reviewed_posts: '涉及活动' }).map(([key, label]) => <Statistic key={key} title={label} value={data.summary[key]} />)}</div>}
        <Form form={form} layout={mobile ? 'vertical' : 'inline'} initialValues={filters} onFinish={values => { setFilters(compactQueryParams(values)); setPage(1); }}>
          <Form.Item name="direction" label="评价方向"><Select allowClear placeholder="全部评价" options={[{ value: 'submitted', label: '我提交的' }, { value: 'received', label: '我收到的' }]} /></Form.Item>
          <Form.Item name="post_id" label="帖子编号" rules={[resourceIdRule('帖子编号')]}><Input maxLength={19} placeholder="全部活动" /></Form.Item>
          <Form.Item><Button type="primary" htmlType="submit" disabled={loading}>筛选</Button></Form.Item>
          {!mobile && <Form.Item><Button disabled={loading} onClick={() => load().catch(() => {})}>刷新</Button></Form.Item>}
        </Form>
      </section>
      {loading ? <Skeleton active /> : error ? <Alert type="error" title={error} action={<Button onClick={() => load().catch(() => {})}>重试</Button>} /> : <>
        {!data?.data.length && <Empty description="暂无符合条件的可见评价" />}
        {data?.data.map(item => <section className="featurePage-panel myReviews-item" key={item.id}>
          <div className="myReviews-item-heading"><Link to={`/posts/${item.post}`}>{item.post_title}</Link><Tag color={item.direction === 'submitted' ? 'blue' : 'cyan'}>{item.direction === 'submitted' ? '我提交的' : '我收到的'}</Tag></div>
          <div className="userIdentity-pair"><UserIdentity user={item.author} /><span aria-hidden="true">→</span><UserIdentity user={item.target} /></div>
          <Rate disabled value={item.rating} /><p className="myReviews-item-comment">{item.comment || '未填写文字评价'}</p>
          <div className="myReviews-item-footer"><Tag color={item.is_revealed ? 'success' : 'processing'}>{item.is_revealed ? '双方评价已揭示' : '本人已提交，等待双方揭示'}</Tag><time>{new Date(item.created_at).toLocaleString('zh-CN')}</time></div>
        </section>)}
        {data?.pagination?.total > 10 && <Pagination current={page} pageSize={10} total={data.pagination.total} showSizeChanger={false} onChange={setPage} />}
      </>}
    </PullToRefresh>
  </FeaturePage>;
};
export default MyReviews;
